import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { useTranslation } from 'react-i18next';
import {
  CurrencyCode,
  Invoice,
  InvoicePayment,
  InvoicePosition,
  Money,
} from '../../../models/Accounting';
import { TFunction } from 'i18next';
import CustomTag from '../../../components/CustomTag';
import {
  CompanyId,
  DateTimeString,
  InvoiceId,
  InvoiceStatus,
  InvoiceType,
  ProjectId,
} from '../../../models/Types';
import { compactDateFormatString, formatMoney } from '../../../util';
import { MENU_BUTTON_SIZE } from '../../../constants';
import PrioSpinner from '../../../components/PrioSpinner';
import moment from 'moment';
import { Classes } from 'jss';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import { VirtualListRowProps } from '@prio365/prio365-react-library/lib/VirtualList/types';
import Flex from '../../../components/Flex';
import {
  GenericSearchResultItem,
  OptimisticWriteFunction,
} from '../../../components/Filter/types';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';
import useProjectsContext from '../../projects/hooks/useProjectsContext';
import useFilterContext from '../../../components/Filter/hooks/useFilterContext';
import FilterContextVirtualTable from '../../../components/Filter/FilterContextVirtualTable';
import FilterResultNoItemsScreen from '../../../components/Filter/FilterResultNoItemsScreen';
import { exportToCsv } from '../../../util/export';
import useExcelExport, { ExcelExportRow } from '../../../hooks/useExcelExport';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    minHeight: '440px',
    height: '100%',
  },
  table: {
    width: '100%',
    whiteSpace: 'nowrap',
    '& .ant-table-thead > tr > th': {
      fontSize: theme.old.typography.fontSize.small,
      fontWeight: theme.old.typography.fontWeight.regular,
    },
  },
  lastDays: {
    marginTop: `${theme.old.spacing.unit(4)}px !important`,
  },
  row: {
    cursor: 'pointer',
    flexDirection: 'row!important',
    '& .prio-vt-cell': {
      flexDirection: 'column',
    },
    '& .prio-vt-cell-content': {
      height: 'unset',
      alignItems: 'center',
      padding: `10px 10px 0`,
    },
  },
  menuColum: {
    padding: '0!important',
    width: MENU_BUTTON_SIZE,
  },
  menuButton: {
    padding: '0!important',
    height: MENU_BUTTON_SIZE,
    width: MENU_BUTTON_SIZE,
    '&:hover': {
      backgroundColor: theme.old.components.table.menuButton.backgroundColor,
      color: theme.old.components.table.menuButton.color,
    },
  },
  tableHeader: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tag: {
    padding: `0 ${theme.old.spacing.baseSpacing}px`,
    height: 24,
  },
  tagText: {
    lineHeight: '24px',
  },
  stateGreen: {
    '&::before': {
      content: '""',
      display: 'block',
      height: '100%',
      backgroundColor: theme.old.palette.chromaticPalette.green,
      width: 5,
    },
  },
  stateRed: {
    '&::before': {
      content: '""',
      display: 'block',
      height: '100%',
      backgroundColor: theme.old.palette.chromaticPalette.red,
      width: 5,
    },
  },
  colorGreen: {
    color: theme.old.palette.chromaticPalette.green,
  },
  colorRed: {
    color: theme.old.palette.chromaticPalette.red,
  },
  textAlignRight: {
    textAlign: 'right',
  },
}));

export type SearchResultInvoicesPage = GenericSearchResultItem<
  InvoicesSearchResult,
  InvoicesSearchResultCalculatedData
>;

export type InvoicesSearchResult = {
  invoiceId: InvoiceId;
  number: string;
  type: InvoiceType;
  status: InvoiceStatus;
  invoiceDate: DateTimeString;
  title: string;
  projectId: ProjectId;
  netSum: Money;
  grossSum: Money;
  recipientCompanyId: CompanyId;
  billerCompanyId: CompanyId;
  invoicePayments: InvoicePayment[];
  invoicePositions: InvoicePosition[];
  isArchived?: boolean;
  rowVersion?: string;
};

export type InvoicesSearchResultCalculatedData = {
  currency: CurrencyCode;
  netTotalSum: number;
  outstandingBalance: number;
  grossTotalSum: number;
  paidBalance: number;
};

export interface InvoiceTableNewRef {
  getInvoices: () => SearchResultInvoicesPage[];
  fetchInvoices: () => void;
  optimisticWrite: OptimisticWriteFunction<
    InvoicesSearchResult,
    InvoicesSearchResultCalculatedData
  >;
}

interface InvoicesTableProps {
  className?: string;
  type?: 'incomingInvoices' | 'outgoingInvoices';
  invoices?: Invoice[];
  onRowClick?: (entry: SearchResultInvoicesPage) => void;
  showPayments: boolean;
  loading?: boolean;
  projectId?: string;
}

export const InvoicesTable = forwardRef(
  (props: InvoicesTableProps, ref: React.Ref<InvoiceTableNewRef>) => {
    //#region ------------------------------ Defaults
    const {
      className,
      type = undefined,
      onRowClick = (record) => {},
      // loading: loadingProp,
      showPayments,
      projectId,
    } = props;
    const classes = useStyles();
    const theme = useTheme<PrioTheme>();
    const { t } = useTranslation();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const {
      data,
      isLoading: isLoadingFilter,
      fetchSearch,
      optimisticWrite,
      tableRef,
    } = useFilterContext<
      InvoicesSearchResult,
      InvoicesSearchResultCalculatedData
    >();

    const _data: Array<SearchResultInvoicesPage> = useMemo(() => {
      return (data?.items ?? []).sort((a, b) => {
        const dateA = moment(b.data?.invoiceDate).unix();
        const dateB = moment(a.data?.invoiceDate).unix();
        if (dateA !== dateB) {
          return dateA - dateB;
        }
        return a.data.number.localeCompare(b.data.number);
      });
    }, [data]);

    const loading = isLoadingFilter;

    const { getCompanyById } = useCompaniesContext();
    const { getProjectById } = useProjectsContext();

    const [selectedInvoices, setSelectedInvoices] = React.useState<
      SearchResultInvoicesPage[]
    >([]);

    const exportExcel = useExcelExport();
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const calcRowHeight = useCallback(
      (value: VirtualListRowProps<SearchResultInvoicesPage>) => {
        const { item } = value;
        if (showPayments && item && item.data?.invoicePayments.length > 0) {
          return 40 + 35 * item.data?.invoicePayments.length;
        }
        return 40;
      },
      [showPayments]
    );

    const handleOnRow = useCallback(
      (record: SearchResultInvoicesPage) => {
        return {
          onClick: onRowClick
            ? () => {
                onRowClick(record);
              }
            : null,
        };
      },
      [onRowClick]
    );

    const classNameTableRow = useCallback(
      (invoice) => {
        return classNames(classes.row, {
          [classes.stateGreen]: invoice.type === 'outgoingInvoice' && !type,
          [classes.stateRed]: invoice.type === 'incomingInvoice' && !type,
        });
      },
      [classes.row, classes.stateGreen, classes.stateRed, type]
    );

    const onExportToCsvClick = useCallback(
      (data: SearchResultInvoicesPage[]) => {
        const toLocaleCurrencyString = (value: number) => {
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        };
        const csvData = data.map((item) => [
          moment(item.data.invoiceDate).format('DD.MM.YYYY'),
          item.data.number,
          getCompanyById(item.data.recipientCompanyId)?.fullName || '',
          item.data.title,
          getProjectById(item.data.projectId)?.name || '',
          `${toLocaleCurrencyString(item.calculated.grossTotalSum)} ${item.calculated.currency}`,
          `${
            item.data.invoicePayments
              ? toLocaleCurrencyString(
                  calcRemainingGross(item, item.data.invoicePayments).value
                )
              : toLocaleCurrencyString(item.data.grossSum.value)
          } ${item.calculated.currency}`,
          `${toLocaleCurrencyString(item.calculated.netTotalSum)} ${item.calculated.currency}`,
          `${
            item.data.invoicePayments
              ? toLocaleCurrencyString(
                  calcRemainingNet(item, item.data.invoicePayments).value
                )
              : toLocaleCurrencyString(item.data.netSum.value)
          } ${item.calculated.currency}`,
          `${t(`accounting:table.tags.${item.data.status}`)}`,
        ]);
        const csvHeaders = [
          t(`accounting:table.invoiceDate`),
          t(`accounting:table.number`),
          t(`accounting:table.recipientCompanyId`),
          t(`accounting:table.title`),
          t(`accounting:table.projectId`),
          t(`accounting:table.grossSum`),
          t(`accounting:table.remainingGross`),
          t(`accounting:table.netSum`),
          t(`accounting:table.remainingNet`),
          t(`accounting:table.status`),
        ];

        exportToCsv(
          csvHeaders,
          csvData,
          `${t(`controlling:navigation.${type === 'incomingInvoices' ? 'incomingInvoices' : 'outgoingInvoices'}`)}.csv`
        );
      },
      [t, getCompanyById, getProjectById, type]
    );

    const onExportToExcelClick = useCallback(
      (data: SearchResultInvoicesPage[]) => {
        const toLocaleCurrencyString = (value: number) => {
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        };

        const exportData: ExcelExportRow[] = data.map((item) => ({
          [t(`accounting:table.invoiceDate`)]: moment(
            item.data.invoiceDate
          ).format('DD.MM.YYYY'),
          [t(`accounting:table.number`)]: item.data.number,
          [t(`accounting:table.recipientCompanyId`)]:
            getCompanyById(item.data.recipientCompanyId)?.fullName || '',
          [t(`accounting:table.title`)]: item.data.title,
          [t(`accounting:table.projectId`)]:
            getProjectById(item.data.projectId)?.name || '',
          [t(`accounting:table.grossSum`)]:
            `${toLocaleCurrencyString(item.calculated.grossTotalSum)} ${item.calculated.currency}`,
          [t(`accounting:table.remainingGross`)]: `${
            item.data.invoicePayments
              ? toLocaleCurrencyString(
                  calcRemainingGross(item, item.data.invoicePayments).value
                )
              : toLocaleCurrencyString(item.data.grossSum.value)
          } ${item.calculated.currency}`,
          [t(`accounting:table.netSum`)]:
            `${toLocaleCurrencyString(item.calculated.netTotalSum)} ${item.calculated.currency}`,
          [t(`accounting:table.remainingNet`)]: `${
            item.data.invoicePayments
              ? toLocaleCurrencyString(
                  calcRemainingNet(item, item.data.invoicePayments).value
                )
              : toLocaleCurrencyString(item.data.netSum.value)
          } ${item.calculated.currency}`,
          [t(`accounting:table.status`)]:
            `${t(`accounting:table.tags.${item.data.status}`)}`,
        }));

        exportExcel(
          exportData,
          t(
            `controlling:navigation.${type === 'incomingInvoices' ? 'incomingInvoices' : 'outgoingInvoices'}`
          ),
          t(
            `controlling:navigation.${type === 'incomingInvoices' ? 'incomingInvoices' : 'outgoingInvoices'}`
          )
        );
      },
      [t, getCompanyById, getProjectById, exportExcel, type]
    );

    //#endregion

    //#region ------------------------------ Columns
    const columns: Column<SearchResultInvoicesPage>[] = useMemo(
      () => [
        {
          title: () => headerItem('invoiceDate', t, classes),
          accessor: 'data.invoiceDate',
          id: 'invoiceDate',
          Cell: ({ value, originalData }) => {
            return (
              value &&
              (showPayments ? (
                <Flex.Column childrenGap={theme.old.spacing.unit(2)}>
                  <div>{compactDateFormatString(value)}</div>
                  {originalData.data?.invoicePayments.map(
                    ({ invoicePaymentId, date }) => (
                      <div
                        key={invoicePaymentId}
                        className={
                          originalData.data?.type === 'incomingInvoice'
                            ? classes.colorGreen
                            : classes.colorRed
                        }
                      >
                        {compactDateFormatString(date)}
                      </div>
                    )
                  )}
                </Flex.Column>
              ) : (
                compactDateFormatString(value)
              ))
            );
          },
          sortingFn: (a, b) =>
            moment(a.data?.invoiceDate).unix() -
            moment(b.data?.invoiceDate).unix(),
          width: 7,
        },
        {
          id: 'number',
          title: () => headerItem('number', t, classes),
          accessor: 'data.number',
          sortingFn: (a, b) => a.data?.number.localeCompare(b.data?.number),
          Cell: ({ value }) => value,
          width: 9,
        },
        {
          title: () => headerItem('recipientCompanyId', t, classes),
          accessor: 'data.recipientCompanyId',
          id: 'recipientCompanyId',
          Cell: ({ value }) => (
            <>
              <p
                style={{
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  margin: 0,
                }}
                title={
                  getCompanyById?.(value)
                    ? getCompanyById?.(value).shortName +
                      ' - ' +
                      (getCompanyById?.(value).fullName ??
                        getCompanyById?.(value).shortName)
                    : ''
                }
              >
                {getCompanyById?.(value)
                  ? getCompanyById?.(value).shortName +
                    ' - ' +
                    (getCompanyById?.(value).fullName ??
                      getCompanyById?.(value).shortName)
                  : ''}
              </p>
            </>
          ),
          sortingFn: (a, b) =>
            getCompanyById?.(
              a.data?.recipientCompanyId
            )?.fullName?.localeCompare(
              getCompanyById?.(b.data?.recipientCompanyId)?.fullName
            ),

          width: 14,
        },
        {
          title: () => headerItem('title', t, classes),
          accessor: 'data.title',
          id: 'title',
          width: 12,
          sortingFn: (a, b) => a.data?.title.localeCompare(b.data?.title),
          Cell: ({ value }) => value,
        },
        {
          title: () => headerItem('projectId', t, classes),
          accessor: 'data.projectId',
          id: 'projectId',
          Cell: ({ value }) =>
            getProjectById?.(value)?.name ?? getProjectById?.(value)?.shortName,
          sortingFn: (a, b) => {
            const projectA = getProjectById?.(a.data?.projectId);
            const projectB = getProjectById?.(b.data?.projectId);
            return (projectA?.name ?? projectA?.shortName ?? '').localeCompare(
              projectB?.name ?? projectB?.shortName ?? ''
            );
          },
          width: 12,
        },
        {
          title: () => headerItem('grossSum', t, classes),
          className: classes.textAlignRight,
          accessor: 'calculated.grossTotalSum',
          id: 'grossTotalSum',
          Cell: (cellProps) => {
            const { value, originalData } = cellProps;
            const _value: Money = {
              value: value,
              isoCode: originalData.calculated?.currency,
            };

            return (
              value &&
              (showPayments ? (
                <Flex.Column childrenGap={theme.old.spacing.unit(2)}>
                  <div>{formatMoney(_value)}</div>
                  {originalData.data?.invoicePayments.map(
                    ({ invoicePaymentId, debitSum, paymentType: type }) => (
                      <div
                        key={invoicePaymentId}
                        className={
                          type === 'payment'
                            ? classes.colorGreen
                            : classes.colorRed
                        }
                      >
                        {formatMoney(debitSum)}
                      </div>
                    )
                  )}
                </Flex.Column>
              ) : (
                formatMoney(_value)
              ))
            );
          },
          sortingFn: (a, b) =>
            a.calculated?.grossTotalSum - b.calculated?.grossTotalSum,
          width: 9,
        },
        {
          title: () => headerItem('remainingGross', t, classes),
          className: classes.textAlignRight,
          accessor: 'data.invoicePayments',
          id: 'invoicePayments1',
          Cell: (cellProps) => {
            const { value, originalData } = cellProps;
            return originalData.calculated?.netTotalSum !== null
              ? formatMoney(
                  value !== null
                    ? calcRemainingGross(originalData, value)
                    : {
                        value: originalData.calculated?.netTotalSum,
                        isoCode: originalData.calculated?.currency,
                      }
                )
              : null;
          },
          sortingFn: (a, b) => {
            const remainingGrossA =
              a.calculated?.netTotalSum !== null
                ? a.data?.invoicePayments !== null
                  ? calcRemainingGross(a, a.data?.invoicePayments).value
                  : a.calculated?.netTotalSum
                : 0;
            const remainingGrossB =
              b.calculated?.netTotalSum !== null
                ? b.data?.invoicePayments !== null
                  ? calcRemainingGross(b, b.data?.invoicePayments).value
                  : b.calculated?.netTotalSum
                : 0;
            return remainingGrossA - remainingGrossB;
          },
          width: 9,
        },
        {
          title: () => headerItem('netSum', t, classes),
          className: classes.textAlignRight,
          accessor: 'calculated.netTotalSum',
          id: 'netTotalSum',
          Cell: ({ value, originalData }) => {
            const _value: Money = {
              value: value,
              isoCode: originalData.calculated?.currency,
            };
            return value ? formatMoney(_value) : 0;
          },
          sortingFn: (a, b) =>
            a.calculated?.netTotalSum - b.calculated?.netTotalSum,
          width: 9,
        },
        {
          title: () => headerItem('remainingNet', t, classes),
          className: classes.textAlignRight,
          accessor: 'data.invoicePayments',
          id: 'invoicePayments2',
          Cell: (cellProps) => {
            const { value, originalData } = cellProps;
            return originalData.calculated?.netTotalSum !== null
              ? formatMoney(
                  value !== null
                    ? calcRemainingNet(originalData, value)
                    : originalData.data?.netSum
                )
              : null;
          },
          sortingFn: (a, b) => {
            const remainingNetA =
              a.calculated?.netTotalSum !== null
                ? a.data?.invoicePayments !== null
                  ? calcRemainingNet(a, a.data?.invoicePayments).value
                  : a.calculated?.netTotalSum
                : 0;
            const remainingNetB =
              b.calculated?.netTotalSum !== null
                ? b.data?.invoicePayments !== null
                  ? calcRemainingNet(b, b.data?.invoicePayments).value
                  : b.calculated?.netTotalSum
                : 0;
            return remainingNetA - remainingNetB;
          },
          width: 9,
        },
        {
          title: () => headerItem('status', t, classes),
          accessor: 'data.status',
          id: 'status',
          Cell: (cellProps) => {
            const { value, originalData } = cellProps;
            return (
              value &&
              (showPayments ? (
                <Flex.Column
                  childrenGap={theme.old.spacing.unit(2)}
                  alignItems="flex-start"
                >
                  <CustomTag
                    type="text"
                    className={classes.tag}
                    textClassName={classes.tagText}
                    color={tagBackground(value, theme)}
                    text={t(`accounting:table.tags.${value}`)}
                    value={{
                      name: t(`accounting:table.tags.${value}`),
                      value,
                    }}
                  />
                  {originalData.data?.invoicePayments.map(
                    ({ invoicePaymentId, paymentType: type }) => (
                      <div
                        key={invoicePaymentId}
                        className={
                          type === 'payment'
                            ? classes.colorGreen
                            : classes.colorRed
                        }
                      >
                        {t(`accounting:invoicePaymentType.${type}`)}
                      </div>
                    )
                  )}
                </Flex.Column>
              ) : (
                <CustomTag
                  type="text"
                  className={classes.tag}
                  textClassName={classes.tagText}
                  color={tagBackground(value, theme)}
                  text={t(`accounting:table.tags.${value}`)}
                  value={{
                    name: t(`accounting:table.tags.${value}`),
                    value,
                  }}
                />
              ))
            );
          },
          sortingFn: (a, b) => a.data?.status.localeCompare(b.data?.status),
          width: 10,
        },
      ],
      [classes, getCompanyById, getProjectById, showPayments, theme, t]
    );
    //#endregion

    //#region ------------------------------ Effects
    useEffect(() => {
      if (tableRef.current) {
        tableRef.current?.forceUpdate();
        tableRef.current?.recomputeRowHeights();
      }
    }, [showPayments, _data, tableRef]);

    useImperativeHandle(ref, () => ({
      getInvoices: () => _data,
      fetchInvoices: () => {
        fetchSearch();
      },
      optimisticWrite,
    }));
    //#endregion

    return (
      <div className={classNames(classes.root, className)}>
        <FilterContextVirtualTable<SearchResultInvoicesPage>
          id={`invoices-table${projectId ? `-${projectId}` : ''}`}
          className={classes.table}
          columns={columns}
          data={_data}
          rowHeight={calcRowHeight}
          classNameTableRow={classNameTableRow}
          onRow={handleOnRow}
          onCheckEquality={(a, b) => a.data?.invoiceId === b.data?.invoiceId}
          loading={
            loading && {
              type: _data?.length > 0 ? 'overlay' : 'noItems',
              indicator: <PrioSpinner alignSelf />,
            }
          }
          onSelectionChange={setSelectedInvoices}
          resizable="relative"
          noItemsScreen={<FilterResultNoItemsScreen />}
          rowsAreSelectable
          actionBarDefaultButtons={
            _data.length > 0
              ? [
                  {
                    children: t('common:actions.csvExport'),
                    iconProp: 'file-arrow-down',
                    onClick: () => {
                      onExportToCsvClick(_data);
                    },
                    style: { display: 'block' },
                  },
                  {
                    children: t('common:actions.excelExport'),
                    iconProp: 'file-excel',
                    onClick: () => {
                      onExportToExcelClick(_data);
                    },
                    style: { display: 'block' },
                  },
                ]
              : []
          }
          actionBarButtons={[
            {
              children: t('common:actions.excelExportSelection'),
              iconProp: 'file-excel',
              onClick: () => {
                onExportToExcelClick(selectedInvoices);
              },
              style: { display: 'block' },
            },
            {
              children: t('common:actions.csvExportSelection'),
              iconProp: 'file-arrow-down',
              onClick: () => {
                onExportToCsvClick(selectedInvoices);
              },
              style: { display: 'block' },
            },
          ]}
        ></FilterContextVirtualTable>
      </div>
    );
  }
);

const headerItem = (itemKey: string, t: TFunction, classes: Classes) => (
  <div className={classes.tableHeader} title={t(`accounting:table.${itemKey}`)}>
    {t(`accounting:table.${itemKey}`)}
  </div>
);

function tagBackground(status: InvoiceStatus, theme: PrioTheme) {
  if (status === 'paid') return theme.old.palette.chromaticPalette.green;
  if (status === 'partlyPaid') return theme.old.palette.primaryColor;
  return theme.old.palette.chromaticPalette.red;
}

const calcRemainingGross: (
  invoice: SearchResultInvoicesPage,
  invoicePayments: InvoicePayment[]
) => Money = (invoice, invoicePayments) => {
  return {
    value:
      invoice.calculated?.grossTotalSum -
      invoicePayments.reduce((sum, value) => sum + value.debitSum.value, 0),
    isoCode: invoice.calculated?.currency,
  };
};

const calcRemainingNet: (
  invoice: SearchResultInvoicesPage,
  invoicePayments: InvoicePayment[]
) => Money = (invoice, invoicePayments) => {
  const remainingGross = calcRemainingGross(invoice, invoicePayments);
  const x =
    invoice.calculated?.grossTotalSum !== 0
      ? invoice.calculated?.netTotalSum / invoice.calculated?.grossTotalSum
      : 1;
  return {
    value: remainingGross.value * x,
    isoCode: invoice.calculated?.currency,
  };
};

export default InvoicesTable;
